/**
 * ONE function that reads a customer's requested items out of a mail — body and attachments —
 * without a model, a server, or an endpoint that does not exist.
 *
 * This is the entry point the RFQ side calls. Everything under it is already built and tested:
 *
 *     .xlsx / .xls     `sheetExtract.ts`   header by label, else by SHAPE
 *     .pdf             `pdfItems.ts`       line number + quantity beside a unit
 *     the mail body    `mailTables.ts`     every <tr> in document order, same column logic
 *
 * ---- order, and why it is that order ----
 *
 * Attachments first, body last. A requisition sent as a spreadsheet is usually ALSO pasted into
 * the mail as a courtesy, and reading both would order everything twice. The attachment is the
 * authoritative copy — it is the document the vessel filled in — so the body is only read when no
 * attachment yielded anything.
 *
 * ---- what it refuses to do ----
 *
 * It never returns an empty list as if it were an answer. `items: []` with `readable: false` means
 * "nothing here could be read", which is a different thing from "this customer wants nothing", and
 * the screen must say so rather than open an empty enquiry that looks successful.
 *
 * It never invents. Codes and units are carried exactly as the customer wrote them, mistakes and
 * all — the Tire Fender requisition on this bench really does measure a mounting lug in `Bottles`,
 * and a reviewer catching that is worth more than a parser quietly improving it.
 */

import { readItemsFromMailBody } from './mailTables'
import { readItemsFromPdfText } from './pdfItems'
import { readItemsFromWorkbook, type SheetItem, type WorkbookSheet } from './sheetExtract'

/** Where a line came from, so the screen can tell a purchaser what to check. */
export type ItemSource = 'spreadsheet' | 'pdf' | 'mail body' | 'attachments'

export interface ReadItemsResult {
  items: SheetItem[]
  /** False when nothing could be read — NOT the same as an order for nothing. */
  readable: boolean
  source: ItemSource | null
  /** Attachments that could not be read, and why — shown, never swallowed. */
  skipped: { name: string; reason: string }[]
  /** Which sheets of a workbook contributed, when the source was a spreadsheet. */
  sheets?: { from: string[]; duplicates: number }
  /**
   * Rows that named an item but carried no quantity — reported, never silently dropped.
   *
   * `MV Kaifan General Purchase List` has one: `Diesel · For powering gensets · (no QTY) · 1 m3
   * ISO Tanks x 6 nos.` The quantity is in the specification rather than the column, so no parser
   * should invent it — but seven rows going in and six coming out, with nothing saying which, is
   * how a customer's requirement disappears.
   */
  needsQty?: string[]
}

/** What the caller must supply per attachment; the reading itself happens here. */
export interface ReadableAttachment {
  name: string
  /** `xlsx` / `pdf` / anything else, which is skipped with a reason. */
  kind: string
  /** As `MailAttachment.size` gives it — `"13 KB"`, `"1.2 MB"`. Used only to tell a signature
   *  logo from a photographed requisition; absent means "assume it matters". */
  size?: string
  /**
   * Every SHEET of a spreadsheet the caller has already opened, in workbook order — not just the
   * first. A requisition routinely runs onto a second sheet, and reading only the first silently
   * halves the order.
   */
  sheets?: WorkbookSheet[]
  /** Extracted text, for a PDF the caller has already read. */
  text?: string
  /**
   * The caller judged the text layer's column read poor (`pdfReadQuality.ts`) and has no
   * Document Intelligence to do better: the text is kept for the AI rescue, but not read into
   * lines here — five wrong lines stop the rescue that none would trigger (2026-09-22).
   */
  unreliable?: boolean
}

/**
 * Keyed on `MailAttachment.kind`'s ACTUAL vocabulary — `xlsx | pdf | doc | img | file`. It was
 * keyed on `image`, a value that union has never held, so every photographed requisition fell
 * through to the generic message and nobody was told a vision model is what it needs. Since
 * 2026-09-17 a photo IS read (Azure Document Intelligence, `useMailExtraction.ts`); one that
 * lands here is a photo the OCR made nothing of, or a deployment without it.
 */
const NOT_READABLE: Record<string, string> = {
  img: 'a photograph that could not be read',
  doc: 'Word documents are not read yet',
  file: 'not a file type we read',
  other: 'not a file type we read',
}

/**
 * A signature logo, rather than something the customer meant us to read.
 *
 * Measured on this bench: 26 attachments are Outlook's inline `imageNNN.ext` and run 0–13 KB,
 * against ~7 genuine photographs at 75 KB and up. Listing the logos as "not read" buried the one
 * line that mattered under three that did not — the message a purchaser actually saw was mostly
 * `image001…png` and `whip check .jfif`.
 *
 * SIZE, not the name. A customer who PASTES a requisition photo into Outlook also gets
 * `image001.png`, so filtering by name would hide exactly the case worth reporting. Anything at
 * or above the threshold is reported however it is named, and an unparseable size is reported too
 * — silence is only for what we are confident is decoration.
 */
const DECORATION_MAX_KB = 20

export function isProbablyDecoration(attachment: Pick<ReadableAttachment, 'kind' | 'size'>): boolean {
  if (attachment.kind !== 'img') return false
  const match = /^([\d.]+)\s*(B|KB|MB)$/i.exec((attachment.size ?? '').trim())
  if (!match) return false
  const value = Number(match[1])
  if (!Number.isFinite(value)) return false
  const kb = match[2].toUpperCase() === 'B' ? value / 1024 : match[2].toUpperCase() === 'MB' ? value * 1024 : value
  return kb < DECORATION_MAX_KB
}

/**
 * Read EVERY attachment on this mail, then the body if none of them yielded anything.
 *
 * It used to stop at the first attachment that produced items. A mail carrying two requisitions —
 * one per vessel, or deck and engine as separate files — had its second file read not at all: not
 * merged, not skipped, not mentioned. The lines that were dropped looked exactly like the lines
 * that were never there.
 *
 * Every line is stamped with the FILE it came from (and the sheet, if the file had several), so
 * the screen can offer one enquiry per requisition. Nothing is deduplicated across files: two
 * attachments are two documents, and a customer who attaches the same order twice meant something
 * by it that a parser should not decide.
 *
 * The BODY is still read only when no attachment gave anything — a requisition sent as a file is
 * usually pasted into the mail as a courtesy too, and reading both orders everything twice.
 */
export function readMailItems(bodyHtml: string, attachments: readonly ReadableAttachment[]): ReadItemsResult {
  const skipped: { name: string; reason: string }[] = []
  const items: SheetItem[] = []
  const sources = new Set<ItemSource>()
  const needsQty: string[] = []
  let duplicates = 0
  const from: string[] = []

  for (const attachment of attachments) {
    if (attachment.kind === 'xlsx' && attachment.sheets) {
      const workbook = readItemsFromWorkbook(attachment.sheets)
      if (workbook.items.length > 0) {
        items.push(...workbook.items.map((item) => ({ ...item, file: attachment.name })))
        sources.add('spreadsheet')
        duplicates += workbook.duplicates
        from.push(...workbook.from)
        needsQty.push(...workbook.skippedForQty)
        continue
      }
      skipped.push({
        name: attachment.name,
        reason:
          attachment.sheets.length > 1
            ? `no item table on any of its ${attachment.sheets.length} sheets`
            : 'no item table found in the sheet',
      })
      continue
    }

    if (attachment.kind === 'pdf' && typeof attachment.text === 'string') {
      if (attachment.unreliable) {
        skipped.push({ name: attachment.name, reason: 'the PDF’s text does not line up with its numbering — read by the AI reader instead' })
        continue
      }
      const read = readItemsFromPdfText(attachment.text)
      if (read) {
        items.push(...read.map((item) => ({ ...item, file: attachment.name })))
        sources.add('pdf')
        continue
      }
      skipped.push({
        name: attachment.name,
        // The distinction matters: one is a scan nothing can read, the other is a layout we missed.
        reason: attachment.text.trim() === '' ? 'a scanned PDF — no text to read' : 'no item lines found in the PDF',
      })
      continue
    }

    /*
      A spreadsheet the caller could not OPEN — it arrives as an `xlsx` attachment with no
      `sheets` at all, because the parser rejected the file before any rows existed.

      `.xls` is the common case: the old binary format is classified as `xlsx` (same icon, same
      intent) but `read-excel-file` rejects it deliberately, since it reads the `.xlsx` zip
      container and a `.xls` is an OLE2 compound file. Three real ones are on this bench and all
      three are customer requisitions.

      Until now this fell through BOTH branches above and was never even listed as skipped, so a
      mail carrying only an `.xls` reported "nothing could be read" without naming the requisition
      that was sitting in it. Reported now, with the one action that fixes it.
    */
    if (attachment.kind === 'xlsx') {
      const legacy = attachment.name.toLowerCase().endsWith('.xls')
      skipped.push({
        name: attachment.name,
        reason: legacy
          ? 'a legacy .xls workbook — re-save it as .xlsx and it will be read'
          : 'the spreadsheet could not be opened',
      })
      continue
    }

    if (attachment.kind !== 'pdf') {
      if (isProbablyDecoration(attachment)) continue
      skipped.push({ name: attachment.name, reason: NOT_READABLE[attachment.kind] ?? NOT_READABLE.other })
    }
  }

  if (items.length > 0) {
    const source: ItemSource = sources.size === 1 ? [...sources][0] : 'attachments'
    return { items, readable: true, source, skipped, sheets: { from, duplicates }, needsQty }
  }

  const fromBody = readItemsFromMailBody(bodyHtml)
  if (fromBody) return { items: fromBody, readable: true, source: 'mail body', skipped }

  return { items: [], readable: false, source: null, skipped }
}

/**
 * One line, worded for a purchaser: what was read, from where, and what was not.
 *
 * Kept beside the reader so the wording cannot drift from the result it describes.
 */
export function describeRead(result: ReadItemsResult): string {
  const missed = result.skipped.length
    ? ` Not read: ${result.skipped.map((s) => `${s.name} (${s.reason})`).join(', ')}.`
    : ''

  if (!result.readable) {
    /*
      NOT "enter the lines by hand". That sat directly above a button offering to read the mail
      with AI, and told the purchaser to do the slowest possible thing instead of pressing it.

      It is also often untrue. `readItemsDirectly` returns nothing both when there is no table AND
      when the table's columns are a genuine tie it refuses to guess at (`inferColumnsByShape`) —
      the second is eminently readable, just not by matching columns.
    */
    return `Could not read this mail by matching columns — read it with AI below, or enter the lines by hand.${missed}`
  }

  const n = result.items.length
  /*
    Naming the sheets matters when there is more than one: "12 lines from the spreadsheet" hides
    whether the second sheet was read at all, which is exactly the question this feature exists to
    answer.
  */
  const sheets = result.sheets
  const where =
    sheets && sheets.from.length > 1 ? `the spreadsheet (sheets ${sheets.from.join(', ')})` : `the ${result.source}`
  /*
    CAPPED. A 39-line read once reported "713 rows named an item with no quantity" and then listed
    all 713, filling the screen and burying the counts that matter (reported 2026-09-07).

    The number is the actionable part; a few names are enough to recognise which rows are meant.
    Same treatment, and same reason, as `summariseUnmatched` gives the UOM and item-code notes.
  */
  const NAMES_SHOWN = 4
  const needsQty = result.needsQty ?? []
  const noQtyNames = needsQty.slice(0, NAMES_SHOWN).map((d) => d.split(' — ')[0])
  const noQtyRest = needsQty.length - noQtyNames.length
  const noQty =
    needsQty.length > 0
      ? ` ${needsQty.length} line${needsQty.length === 1 ? '' : 's'} came through with no quantity (set ${needsQty.length === 1 ? 'it' : 'them'} on the RFQ builder) — ${noQtyNames.join(', ')}${
          noQtyRest > 0 ? `, and ${noQtyRest} more` : ''
        }.`
      : ''

  const restated =
    sheets && sheets.duplicates > 0
      ? ` ${sheets.duplicates} line${sheets.duplicates === 1 ? '' : 's'} restated on another sheet were counted once.`
      : ''

  /*
    "no AI needed" was a lie of implication — it reads as "AI was available and we did better".
    There is no AI in this path at ALL: `mail.extract_enquiry_items` dies on `B1`, so
    `runExtraction` makes no server call and every line here is matched structurally, by column
    position and a unit vocabulary.

    That distinction is the whole point of the note. A structural read can be confidently wrong in
    a way a purchaser will not catch by glancing — one mail put an item code where the quantity
    belonged and asked for 811,676 metres of brake lining, reported under the old wording without
    anyone doubting it. So the note now names the method and asks for the one check that catches
    that class of error.
  */
  return `Read ${n} line${n === 1 ? '' : 's'} from ${where} by matching columns, not by AI — check the quantities.${noQty}${restated}${missed}`
}
